<template>
  <Modal :show="show" title="Edit Supplier" size="lg" @close="handleClose">
    <form @submit.prevent="handleSubmit" class="space-y-4">
      <div>
        <label for="es-suppliername" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
          Supplier Name <span class="text-red-500" aria-hidden="true">*</span><span class="sr-only">(required)</span>
        </label>
        <input
          id="es-suppliername"
          v-model="form.suppliername"
          type="text"
          required
          autocomplete="organization"
          class="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>

      <div>
        <label for="es-contactname" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
          Contact Person
        </label>
        <input
          id="es-contactname"
          v-model="form.contactname"
          type="text"
          autocomplete="name"
          class="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>

      <div>
        <label for="es-contactemail" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
          Contact Email
        </label>
        <input
          id="es-contactemail"
          v-model="form.contactemailaddress"
          type="email"
          autocomplete="email"
          class="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>

      <div>
        <label for="es-opsemail" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
          Ops/Receipt Email
        </label>
        <input
          id="es-opsemail"
          v-model="form.emailaddress"
          type="email"
          autocomplete="email"
          placeholder="Email for collection receipts"
          class="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>

      <div>
        <label for="es-phone" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
          Phone Number
        </label>
        <input
          id="es-phone"
          v-model="form.contacttelephone"
          type="tel"
          autocomplete="tel"
          class="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>

      <!-- Error Message -->
      <div v-if="errorMessage" role="alert" class="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded-lg">
        <p class="text-sm text-red-700 dark:text-red-300">{{ errorMessage }}</p>
      </div>
    </form>

    <template #footer>
      <div class="flex items-center justify-end gap-3">
        <button
          @click="handleClose"
          :disabled="isSaving"
          type="button"
          class="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-neutral-100 dark:bg-neutral-700 rounded-lg hover:bg-neutral-200 dark:hover:bg-neutral-600 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          @click="handleSubmit"
          :disabled="isSaving"
          type="button"
          class="px-4 py-2 text-sm font-medium text-white bg-primary-700 rounded-lg hover:bg-primary-800 transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          <svg
            v-if="isSaving"
            class="animate-spin h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span>Save</span>
        </button>
      </div>
    </template>
  </Modal>
</template>

<script>
import { ref, watch } from 'vue'
import { useStore } from 'vuex'
import axios from 'axios'
import Modal from '@/components/ui/Modal.vue'
import useToast from '@/composables/useToast'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default {
  name: 'EditSupplierModal',
  components: { Modal },
  props: {
    show: {
      type: Boolean,
      required: true
    },
    supplier: {
      type: Object,
      required: true
    },
    supplierId: {
      type: String,
      required: true
    }
  },
  emits: ['close', 'saved'],
  setup(props, { emit }) {
    const store = useStore()
    const apiUrl = store.state.apiUrl
    const { showToast } = useToast()

    const form = ref({
      suppliername: '',
      contactname: '',
      contactemailaddress: '',
      emailaddress: '',
      contacttelephone: ''
    })

    const isSaving = ref(false)
    const errorMessage = ref('')

    const resetForm = () => {
      form.value = {
        suppliername: props.supplier?.suppliername || '',
        contactname: props.supplier?.contactname || '',
        contactemailaddress: props.supplier?.contactemailaddress || '',
        emailaddress: props.supplier?.emailaddress || '',
        contacttelephone: props.supplier?.contacttelephone || ''
      }
      errorMessage.value = ''
    }

    watch(() => props.show, (newVal) => {
      if (newVal) {
        resetForm()
      }
    })

    // Ignore close requests (Cancel, X, Escape) while a save is in flight.
    const handleClose = () => {
      if (isSaving.value) return
      emit('close')
    }

    const validate = () => {
      if (!form.value.suppliername.trim()) {
        return 'Supplier name is required.'
      }
      if (form.value.contactemailaddress && !EMAIL_RE.test(form.value.contactemailaddress)) {
        return 'Contact email is not a valid email address.'
      }
      if (form.value.emailaddress && !EMAIL_RE.test(form.value.emailaddress)) {
        return 'Ops/receipt email is not a valid email address.'
      }
      return ''
    }

    const handleSubmit = async () => {
      const validationError = validate()
      if (validationError) {
        errorMessage.value = validationError
        return
      }

      isSaving.value = true
      errorMessage.value = ''

      try {
        // Build payload with only changed fields
        const payload = {}
        const fields = ['suppliername', 'contactname', 'contactemailaddress', 'emailaddress', 'contacttelephone']

        for (const field of fields) {
          if (form.value[field] !== (props.supplier?.[field] || '')) {
            payload[field] = form.value[field]
          }
        }

        if (Object.keys(payload).length === 0) {
          emit('saved')
          emit('close')
          return
        }

        await axios.put(`${apiUrl}/suppliers/${props.supplierId}`, payload)

        showToast('success', 'Supplier updated')
        emit('saved')
        emit('close')
      } catch (error) {
        console.error('Error updating supplier:', error)
        errorMessage.value = error.response?.data?.detail || error.message || 'Failed to update supplier'
      } finally {
        isSaving.value = false
      }
    }

    return {
      form,
      isSaving,
      errorMessage,
      handleSubmit,
      handleClose
    }
  }
}
</script>
